import { CheckboxNode } from "common/component/FilterSection/CheckboxFilter/CheckboxFilter";
import { HardwareOrderStatus } from "interfaces";
import { PropertyFilterProps, TableProps } from "@amzn/awsui-components-react";
import { updateUrlParams, useUrlFilterParsers } from "./useUrlParams";
import { useMainCategories } from "hooks/useCatalog";
import { useBuildingsWithParentChild } from "hooks/useBuildings";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { NonCancelableEventHandler } from "@amzn/awsui-components-react/polaris/internal/events";
import { Order } from "@amzn/ito-client";
import { getString } from "common/ui_string_labels/ui-strings-utils";
import { buildQueryObject, getPrimarySelectedLocations, normalizeQuery } from "./filtering-utils";

/**
 * @interface
 * Represents the current sorting state for the order list table.
 * Tracks both UI field names and API-compatible field mappings.
 */
export interface SortingState {
    sortBy?: string;
    sortOrder?: string;
    sortField?: string;
}

/**
 * @interface
 * Basic field filter for simple key-value pairs in query objects.
 */
export interface FieldFilter {
    [key: string]: string | string[];
}

/**
 * @interface
 * MongoDB-style OR operator containing array of field filters or nested OR operators.
 * Enables complex nested query logic.
 */
export interface OrOperator {
    $or: (FieldFilter | OrOperator)[];
}

/**
 * @interface
 * Query object structure for filtering orders with MongoDB-style operators.
 * Supports complex filtering with AND/OR logic and sorting directives.
 */
export interface QueryObject {
    [key: string]: string | string[] | (FieldFilter | OrOperator)[] | undefined;
    $and?: (FieldFilter | OrOperator)[];
    $or?: (FieldFilter | OrOperator)[];
    status?: string[];
    "requested.by"?: string[];
    asc?: string[];
    desc?: string[];
}

/**
 * @interface
 * Groups location selections by type (region, country, city, building).
 * Used for organizing hierarchical location data in URL parameters.
 */
export interface LocationGroup {
    [key: string]: string[];
}

/**
 * Maps user-friendly property names to their nested object paths in order data.
 * Used for property filter configuration and query building.
 */
export const SEARCH_ORDERS_PROPERTY_KEYS = {
    orderId: "orderReference",
    legacyOrderId: "details.legacyOrderId",
    poNumber: "items.subLineItems.poNumber",
    prNumber: "items.subLineItems.prNumber",
    requestCategories: "details.requestCategories",
    itemName: "display",
};

/**
 * Maps UI table column field names to API-compatible field paths.
 * Ensures correct field names are sent to the backend for sorting.
 */
const SORT_FIELD_MAPPINGS: Record<string, string> = {
    requestDate: "requested.on",
    statusHistory: "statusHistory.on",
};

/**
 * Configuration for property filter options in the order search UI.
 * Defines available filter fields, their labels, and supported operators.
 */
const FILTERING_PROPERTIES = [
    {
        key: SEARCH_ORDERS_PROPERTY_KEYS.orderId,
        propertyLabel: "Order ID",
        operators: ["="],
        groupValuesLabel: "Order ID Values",
    },
    {
        key: SEARCH_ORDERS_PROPERTY_KEYS.legacyOrderId,
        propertyLabel: "Legacy Order ID",
        operators: ["="],
        groupValuesLabel: "Legacy Order ID",
    },
    {
        key: SEARCH_ORDERS_PROPERTY_KEYS.poNumber,
        propertyLabel: "PO Number",
        operators: ["="],
        groupValuesLabel: "PO Number Values",
    },
    {
        key: SEARCH_ORDERS_PROPERTY_KEYS.prNumber,
        propertyLabel: "PR Number",
        operators: ["="],
        groupValuesLabel: "PR Number Values",
    },
    {
        key: SEARCH_ORDERS_PROPERTY_KEYS.requestCategories,
        propertyLabel: "Requested Category",
        operators: ["="],
        groupValuesLabel: "Request Categories",
    },
    {
        key: SEARCH_ORDERS_PROPERTY_KEYS.itemName,
        propertyLabel: "Item Name",
        operators: ["="],
        groupValuesLabel: "Item Names",
    },
];

/**
 * Configuration for requester-specific filtering.
 * Separated from main filters to enable independent requester search functionality.
 */
const REQUESTER_FILTERING_PROPERTY = [
    {
        key: "requested.by",
        propertyLabel: "Requester",
        operators: ["="],
        groupValuesLabel: "Requester values",
    },
];

// Base path for localized order status strings
const getStringPathStatuses: string = "common.orderStatuses";

/**
 * Array of checkbox options for order status filtering with localized names.
 * Active statuses (assigned through pending delivery) are selected by default.
 */
export const ORDER_STATUS_OPTIONS: CheckboxNode[] = [
    { id: HardwareOrderStatus.ASSIGNED, name: getString(`${getStringPathStatuses}.assigned`), selected: true },
    {
        id: HardwareOrderStatus.APPROVED,
        name: getString(`${getStringPathStatuses}.approved`),
        selected: true,
    },
    {
        id: HardwareOrderStatus.WORK_IN_PROGRESS,
        name: getString(`${getStringPathStatuses}.workInProgress`),
        selected: true,
    },
    {
        id: HardwareOrderStatus.PENDING_QUOTE,
        name: getString(`${getStringPathStatuses}.pendingQuote`),
        selected: true,
    },
    {
        id: HardwareOrderStatus.PENDING_REQ_INFO,
        name: getString(`${getStringPathStatuses}.pendingReqInfo`),
        selected: true,
    },
    {
        id: HardwareOrderStatus.PENDING_APPROVAL,
        name: getString(`${getStringPathStatuses}.pendingApproval`),
        selected: true,
    },
    {
        id: HardwareOrderStatus.PENDING_DELIVERY,
        name: getString(`${getStringPathStatuses}.pendingDelivery`),
        selected: true,
    },
    {
        id: HardwareOrderStatus.CANCELLED,
        name: getString(`${getStringPathStatuses}.cancelled`),
    },
    {
        id: HardwareOrderStatus.DELIVERED,
        name: getString(`${getStringPathStatuses}.delivered`),
    },
    { id: HardwareOrderStatus.REJECTED, name: getString(`${getStringPathStatuses}.rejected`) },
];

/**
 * Custom hook that manages all filtering options and their state.
 * Provides centralized filter state management for the order list.
 * Initializes filter state from URL parameters to support bookmarking.
 *
 * @returns Object containing all filter options, state, and setter functions
 */
export const OrderFilteringOptions = () => {
    const urlFiltering = useUrlFilterParsers();
    const categories = useMainCategories();
    const { nodes: buildingsData } = useBuildingsWithParentChild();

    // Build category filter options from fetched categories
    const categoryOptions: readonly PropertyFilterProps.FilteringOption[] | undefined = useMemo(() => {
        if (categories.isLoadingCat || categories.errorCat) {
            return undefined;
        }
        return categories.categories?.map((category) => ({
            propertyKey: SEARCH_ORDERS_PROPERTY_KEYS.requestCategories,
            value: category.name,
        }));
    }, [categories.categories, categories.isLoadingCat, categories.errorCat]);

    const [query, setQuery] = useState<PropertyFilterProps.Query>(() => urlFiltering.parseQueryFromUrl());
    const [requesterQuery, setRequesterQuery] = useState<PropertyFilterProps.Query>(() =>
        urlFiltering.parseRequesterFromUrl(),
    );
    const [orderStatus, setOrderStatus] = useState<CheckboxNode[]>(() => urlFiltering.parseStatusFromUrl());
    const [locationSelectedItems, setLocationSelectedItems] = useState<CheckboxNode[]>([]);

    // Populate location filter once buildings data loads
    const buildingsInitialized = useRef(false);
    useEffect(() => {
        if (buildingsData.length > 0 && !buildingsInitialized.current) {
            buildingsInitialized.current = true;
            setLocationSelectedItems(urlFiltering.parseLocationFromUrl(buildingsData));
        }
    }, [buildingsData]);

    const memoizedQuery = useMemo(() => {
        return normalizeQuery(query);
    }, [query]);

    return {
        filteringProperties: FILTERING_PROPERTIES,
        query: memoizedQuery,
        setQuery,
        orderStatusOptions: ORDER_STATUS_OPTIONS,
        requesterQuery,
        setRequesterQuery,
        requesterFilteringProperty: REQUESTER_FILTERING_PROPERTY,
        orderStatus,
        setOrderStatus,
        locationSelectedItems,
        setLocationSelectedItems,
        categoryOptions,
    };
};

/**
 * Main filtering hook that orchestrates all filter operations.
 * Manages URL synchronization, query building, and filter state coordination.
 * Ensures filter state stays in sync with URL for shareable links.
 *
 * @returns Object containing filter options, sorting handlers, and query state
 */
const useOrderFiltering = () => {
    const filterOptions = OrderFilteringOptions();
    const { nodes: buildingsData } = useBuildingsWithParentChild();
    const urlFiltering = useUrlFilterParsers();
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();

    const [query, setQuery] = useState<QueryObject>();
    const [filterLoading, setFilterLoading] = useState(true);
    const [currentSorting, setCurrentSorting] = useState<SortingState>();

    const memoizedGetPrimarySelectedLocations = useMemo(
        () => getPrimarySelectedLocations(filterOptions.locationSelectedItems),
        [filterOptions.locationSelectedItems],
    );

    // Sync URL changes with filter state when user navigates back/forward or shares link
    useEffect(() => {
        if (filterLoading) return;

        const queryFromUrl = urlFiltering.parseQueryFromUrl();
        const requesterFromUrl = urlFiltering.parseRequesterFromUrl();
        const statusFromUrl = urlFiltering.parseStatusFromUrl();
        const locationFromUrl = urlFiltering.parseLocationFromUrl(buildingsData);

        const normalizedUrlQuery = normalizeQuery(queryFromUrl);
        const normalizedCurrentQuery = normalizeQuery(filterOptions.query);

        if (
            JSON.stringify(normalizedUrlQuery.tokens) !== JSON.stringify(normalizedCurrentQuery.tokens) ||
            normalizedUrlQuery.operation !== normalizedCurrentQuery.operation
        ) {
            filterOptions.setQuery(queryFromUrl);
        }

        if (JSON.stringify(requesterFromUrl.tokens) !== JSON.stringify(filterOptions.requesterQuery.tokens)) {
            filterOptions.setRequesterQuery(requesterFromUrl);
        }

        const currentStatusIds = filterOptions.orderStatus.filter((s) => s.selected).map((s) => s.id);
        const newStatusIds = statusFromUrl.filter((s) => s.selected).map((s) => s.id);

        if (JSON.stringify(currentStatusIds) !== JSON.stringify(newStatusIds)) {
            filterOptions.setOrderStatus(statusFromUrl);
        }

        const currentLocationIds = memoizedGetPrimarySelectedLocations;
        const newLocationIds = getPrimarySelectedLocations(locationFromUrl);

        if (JSON.stringify(currentLocationIds) !== JSON.stringify(newLocationIds)) {
            filterOptions.setLocationSelectedItems(locationFromUrl);
        }
    }, [location.search, buildingsData]);

    /**
     * Handles table sorting changes and updates the sorting state.
     * Maps UI sorting fields to API-compatible field names using SORT_FIELD_MAPPINGS.
     *
     * @param e {NonCancelableEventHandler<TableProps.SortingState<Order>>} - Sorting change event from the table component
     */
    const onSortingChange: NonCancelableEventHandler<TableProps.SortingState<Order>> = async (e) => {
        const filterField = e.detail.sortingColumn.sortingField;
        const sortBy = SORT_FIELD_MAPPINGS[filterField as string] || filterField;
        const sortOrder = e.detail.isDescending ? "sort.desc" : "sort.asc";
        setCurrentSorting({ sortField: filterField, sortBy, sortOrder });
    };

    // Main filter effect - builds query object and updates URL when any filter changes
    useEffect(() => {
        setFilterLoading(true);

        const queryObject = buildQueryObject(filterOptions, memoizedGetPrimarySelectedLocations, currentSorting);
        const urlParams = updateUrlParams(filterOptions, memoizedGetPrimarySelectedLocations, currentSorting);

        if (searchParams.toString() !== urlParams.toString()) {
            setSearchParams(urlParams);
        }

        setQuery(queryObject);
        setFilterLoading(false);
    }, [
        filterOptions.query,
        filterOptions.requesterQuery,
        filterOptions.orderStatus,
        filterOptions.locationSelectedItems,
        currentSorting,
    ]);

    return {
        filterOptions,
        sorting: {
            currentSorting,
            onSortingChange,
        },
        filter: {
            query,
            filterLoading,
        },
    };
};

export default useOrderFiltering;
